import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Lock, Baby, Weight, Calendar, Droplet, AlertCircle,
  CheckCircle2, AlertTriangle, Trash2, Plus, Printer, RefreshCw,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Area, ComposedChart, ReferenceLine, Legend,
} from 'recharts';
import { interpret, expectedWeightRange, RECOVERY_DAY_THRESHOLDS } from './clinicalRules';

// ── Contraseña ──
const GATE_KEY = 'kn_lactancia_gate';
const PASSWORD = 'peso'; // cambiable

// ── Estado inicial ──
const emptyCase = () => ({
  babyName: '',
  babySex: '',
  birthDate: '',        // YYYY-MM-DD
  gestationalAgeWeeks: '',
  birthType: '',        // vaginal · cesarea
  complications: {
    cesarea: false, separacion: false, sueros: false,
    ictericia: false, fototerapia: false, hipoglucemia: false,
    internacion: false,
  },
  birthWeight: '',
  dischargeWeight: '',
  dischargeDay: '',
  minWeight: '',
  minWeightDate: '',        // fecha del peso mínimo (YYYY-MM-DD)
  weights: [],           // [{ date, weight, balanza, note }]
  currentWeight: '',
  urineFreq: '',
  stoolFreq: '',
  eliminationNotes: '',
  generalState: '',      // alerta · somnoliento · cuesta_despertar · irritable
  feedingType: '',       // exclusiva · mixta · formula
  notes: '',
});

// ── Utils ──
function daysBetween(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const ms = now - d;
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function daysFromBirth(birthDateStr, targetDateStr) {
  if (!birthDateStr || !targetDateStr) return null;
  const b = new Date(birthDateStr + 'T00:00:00');
  const t = new Date(targetDateStr + 'T00:00:00');
  if (isNaN(b) || isNaN(t)) return null;
  const ms = t - b;
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function num(v) {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return isNaN(n) ? null : n;
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─────────────────────────────────────────────────────────────
// Password gate
// ─────────────────────────────────────────────────────────────
function Gate({ onUnlock }) {
  const [val, setVal] = useState('');
  const [err, setErr] = useState('');
  const submit = (e) => {
    e.preventDefault();
    if (val.trim() === PASSWORD) {
      localStorage.setItem(GATE_KEY, '1');
      onUnlock();
    } else {
      setErr('Contraseña incorrecta.');
    }
  };
  return (
    <div className="min-h-screen bg-[#F4EDE2] grid place-items-center px-5 py-10" data-testid="lact-gate">
      <div className="max-w-[440px] w-full bg-[#FAF4E8] border border-[#DBCEB4] rounded-3xl p-9 shadow-[0_30px_60px_-30px_rgba(74,46,32,0.4)]">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-12 h-12 rounded-full bg-[#E8C9C0] grid place-items-center">
            <Lock size={22} strokeWidth={1.6} className="text-[#B8806E]" />
          </span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#8A7E6E]">
            Uso profesional
          </span>
        </div>
        <h1 className="font-serif text-[28px] leading-tight">
          Asesoría de <span className="italic text-[#B8806E]">lactancia</span>
        </h1>
        <p className="mt-3 text-[13.5px] text-[#2E2A23]/75 leading-relaxed">
          Herramienta interna para Karina Negrin. Ingresá tu contraseña de acceso.
        </p>
        <form onSubmit={submit} className="mt-6">
          <input
            type="password"
            value={val}
            onChange={(e) => { setVal(e.target.value); setErr(''); }}
            placeholder="••••••••"
            autoFocus
            className="w-full bg-white border border-[#C5B496] rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#B8806E]"
            data-testid="lact-gate-input"
          />
          {err && <p className="mt-3 text-[13px] text-[#9A6555]">{err}</p>}
          <button className="btn-primary mt-5 w-full justify-center" data-testid="lact-gate-submit">
            Ingresar
          </button>
        </form>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase text-[#8A7E6E] hover:text-[#B8806E]">
          <ArrowLeft size={13} strokeWidth={1.7} /> Volver al sitio
        </Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Field helper
// ─────────────────────────────────────────────────────────────
function Field({ label, hint, children, cols = 1 }) {
  return (
    <label className={`block ${cols === 2 ? 'sm:col-span-2' : ''}`}>
      <span className="block text-[11px] tracking-[0.14em] uppercase text-[#8A7E6E] mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-[11.5px] text-[#8A7E6E]/80 mt-1">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full bg-white border border-[#DBCEB4] rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-[#B8806E] focus:ring-1 focus:ring-[#B8806E]/30";

function TextInput({ value, onChange, ...rest }) {
  return <input className={inputCls} value={value || ''} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

function NumberInput({ value, onChange, ...rest }) {
  return <input type="number" className={inputCls} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

function Select({ value, onChange, options }) {
  return (
    <select className={inputCls} value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">— elegir —</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────
// Complications toggles
// ─────────────────────────────────────────────────────────────
function CompsBoxes({ value, onChange }) {
  const items = [
    { key: 'cesarea', label: 'Cesárea' },
    { key: 'sueros', label: 'Sueros IV maternos' },
    { key: 'separacion', label: 'Separación madre-bebé' },
    { key: 'ictericia', label: 'Ictericia' },
    { key: 'fototerapia', label: 'Fototerapia' },
    { key: 'hipoglucemia', label: 'Hipoglucemia' },
    { key: 'internacion', label: 'Internación neonatal' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
      {items.map(it => {
        const active = value?.[it.key];
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onChange({ ...value, [it.key]: !active })}
            className={`text-left text-[12.5px] px-3 py-2 rounded-lg border transition ${
              active
                ? 'bg-[#B8806E] border-[#B8806E] text-white'
                : 'bg-white border-[#DBCEB4] text-[#2E2A23] hover:border-[#B8806E]'
            }`}
            data-testid={`lact-comp-${it.key}`}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Weights list
// ─────────────────────────────────────────────────────────────
function WeightsList({ weights, onChange, birthDate }) {
  const add = () => onChange([...weights, { date: '', weight: '', balanza: '', note: '' }]);
  const remove = (i) => onChange(weights.filter((_, idx) => idx !== i));
  const update = (i, k, v) => onChange(weights.map((w, idx) => idx === i ? { ...w, [k]: v } : w));

  return (
    <div className="space-y-2">
      {weights.length === 0 && (
        <p className="text-[13px] text-[#8A7E6E] italic">
          Agregá pesos posteriores conforme los tengas.
        </p>
      )}
      {weights.map((w, i) => {
        const dayCalc = birthDate && w.date ? daysFromBirth(birthDate, w.date) : null;
        return (
          <div key={i} className="grid grid-cols-[1fr_110px_1fr_36px] gap-2 items-center">
            <div>
              <input
                type="date"
                className={inputCls}
                value={w.date || ''}
                onChange={(e) => update(i, 'date', e.target.value)}
              />
              {dayCalc != null && (
                <span className="block text-[10.5px] text-[#8A7E6E]/80 mt-0.5 pl-1">día {dayCalc}</span>
              )}
            </div>
            <NumberInput
              value={w.weight}
              onChange={(v) => update(i, 'weight', v)}
              placeholder="peso (g)"
            />
            <select
              className={inputCls}
              value={w.balanza || ''}
              onChange={(e) => update(i, 'balanza', e.target.value)}
            >
              <option value="">— balanza —</option>
              <option value="misma">Misma balanza</option>
              <option value="otra">Otra balanza</option>
            </select>
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-[#9A6555] hover:text-[#B8806E] grid place-items-center"
              aria-label="quitar"
            >
              <Trash2 size={16} />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#B8806E] hover:text-[#9A6555] mt-1"
        data-testid="lact-add-weight"
      >
        <Plus size={14} strokeWidth={1.8} /> Agregar peso
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Chart: bebé + banda esperada
// ─────────────────────────────────────────────────────────────
function WeightChart({ caseData, weights, ageInDays }) {
  const birthWeight = num(caseData.birthWeight);
  const minWeightDay = caseData.birthDate && caseData.minWeightDate
    ? daysFromBirth(caseData.birthDate, caseData.minWeightDate)
    : null;
  const currentWeight = num(caseData.currentWeight);

  const chartData = useMemo(() => {
    if (!birthWeight) return [];
    // Puntos del bebé: día 0 (nacimiento), día del alta, día del peso mínimo, pesos posteriores, peso actual
    const pts = [{ day: 0, bebe: birthWeight }];
    if (num(caseData.dischargeWeight) && num(caseData.dischargeDay) != null)
      pts.push({ day: num(caseData.dischargeDay), bebe: num(caseData.dischargeWeight) });
    if (num(caseData.minWeight) && minWeightDay != null)
      pts.push({ day: minWeightDay, bebe: num(caseData.minWeight) });
    weights.forEach(w => {
      const d = num(w.day), p = num(w.weight);
      if (d != null && p != null) pts.push({ day: d, bebe: p });
    });
    if (currentWeight && ageInDays != null) pts.push({ day: ageInDays, bebe: currentWeight });

    // Deduplicar por día (última medición prevalece) y ordenar
    const byDay = new Map();
    pts.forEach(p => byDay.set(p.day, p));
    const sorted = [...byDay.values()].sort((a, b) => a.day - b.day);

    // Interpolar la banda esperada para cada día presente + rellenar en pasos
    const maxDay = Math.max(...sorted.map(p => p.day), ageInDays || 30);
    const allDays = [];
    for (let d = 0; d <= maxDay; d += Math.max(1, Math.floor(maxDay / 40))) allDays.push(d);
    if (!allDays.includes(maxDay)) allDays.push(maxDay);

    // Merge puntos del bebé + banda esperada
    return allDays.map(d => {
      const point = sorted.find(p => p.day === d) || {};
      const range = expectedWeightRange(birthWeight, d);
      return {
        day: d,
        bebe: point.bebe ?? null,
        min: Math.round(range.min),
        ideal: Math.round(range.ideal),
      };
    });
  }, [birthWeight, currentWeight, minWeightDay, ageInDays, caseData.dischargeWeight, caseData.dischargeDay, caseData.minWeight, weights]);

  if (!birthWeight) {
    return (
      <div className="h-[280px] grid place-items-center text-[#8A7E6E] text-[13px] italic border border-dashed border-[#DBCEB4] rounded-2xl bg-[#FAF4E8]">
        Ingresá el peso de nacimiento para ver la curva.
      </div>
    );
  }

  return (
    <div className="h-[320px] bg-[#FAF4E8] border border-[#DBCEB4] rounded-2xl p-3">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: 10, right: 12, bottom: 8, left: 0 }}>
          <defs>
            <linearGradient id="expectedBand" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8FA08A" stopOpacity="0.35"/>
              <stop offset="100%" stopColor="#8FA08A" stopOpacity="0.08"/>
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#DBCEB4" strokeDasharray="3 3"/>
          <XAxis
            dataKey="day"
            type="number"
            domain={[0, 'dataMax']}
            tick={{ fontSize: 11, fill: '#8A7E6E' }}
            label={{ value: 'días', position: 'insideBottomRight', offset: -4, fill: '#8A7E6E', fontSize: 10 }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#8A7E6E' }}
            label={{ value: 'gramos', angle: -90, position: 'insideLeft', fill: '#8A7E6E', fontSize: 10 }}
            domain={['dataMin - 100', 'dataMax + 100']}
          />
          <Tooltip
            contentStyle={{ background: '#FAF4E8', border: '1px solid #DBCEB4', borderRadius: 8, fontSize: 12 }}
            labelFormatter={(d) => `Día ${d}`}
            formatter={(value, name) => {
              const labels = { bebe: 'Bebé', ideal: 'Esperado ideal', min: 'Esperado mínimo' };
              return value == null ? ['—', labels[name]] : [`${value} g`, labels[name]];
            }}
          />
          {/* Banda esperada verde */}
          <Area type="monotone" dataKey="ideal" stroke="none" fill="url(#expectedBand)" isAnimationActive={false}/>
          <Line type="monotone" dataKey="min" stroke="#8FA08A" strokeDasharray="4 4" dot={false} strokeWidth={1.2} name="Esperado mínimo" isAnimationActive={false}/>
          <Line type="monotone" dataKey="ideal" stroke="#6B7C66" strokeDasharray="4 4" dot={false} strokeWidth={1.2} name="Esperado ideal" isAnimationActive={false}/>
          {/* Línea del bebé */}
          <Line type="monotone" dataKey="bebe" stroke="#B8806E" strokeWidth={2.4} dot={{ r: 4, fill: '#B8806E' }} connectNulls name="Bebé" isAnimationActive={false}/>
          <ReferenceLine
            y={birthWeight}
            stroke="#9A6555" strokeDasharray="2 4" strokeWidth={1}
            label={{ value: 'Peso nac.', position: 'right', fill: '#9A6555', fontSize: 10 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Findings box
// ─────────────────────────────────────────────────────────────
function FindingsBox({ findings }) {
  const groups = [
    { key: 'favorable', label: 'Signos favorables', color: '#8FA08A', bg: '#EFF3EA', Icon: CheckCircle2 },
    { key: 'indagar', label: 'Puntos a valorar / indagar', color: '#B8806E', bg: '#F7ECE6', Icon: AlertCircle },
    { key: 'alarma', label: 'Signos de alarma · sugerir interconsulta', color: '#B44343', bg: '#F5E1E1', Icon: AlertTriangle },
  ];
  const nothing = !findings.favorable.length && !findings.indagar.length && !findings.alarma.length;
  if (nothing) {
    return (
      <div className="text-[13px] text-[#8A7E6E] italic border border-dashed border-[#DBCEB4] rounded-2xl bg-[#FAF4E8] p-6 text-center">
        Completá los datos del formulario y las interpretaciones aparecerán acá en tiempo real.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="lact-findings">
      {groups.map(g => (
        findings[g.key].length > 0 && (
          <div key={g.key} className="rounded-2xl border border-[#DBCEB4] overflow-hidden">
            <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: g.bg }}>
              <g.Icon size={16} strokeWidth={1.9} style={{ color: g.color }}/>
              <span className="text-[11.5px] tracking-[0.14em] uppercase font-medium" style={{ color: g.color }}>
                {g.label}
              </span>
              <span className="ml-auto text-[11px]" style={{ color: g.color }}>
                {findings[g.key].length}
              </span>
            </div>
            <ul className="px-4 py-3 space-y-2 bg-[#FAF4E8]">
              {findings[g.key].map((f, i) => (
                <li key={i} className="text-[13.5px] leading-[1.55] text-[#2E2A23] flex gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full flex-none" style={{ background: g.color }}/>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────
export default function AsesoriaLactancia() {
  const [unlocked, setUnlocked] = useState(false);
  const [c, setC] = useState(emptyCase());

  useEffect(() => { setUnlocked(localStorage.getItem(GATE_KEY) === '1'); }, []);

  // helpers
  const set = (k, v) => setC(prev => ({ ...prev, [k]: v }));
  const setComps = (v) => setC(prev => ({ ...prev, complications: v }));

  const ageInDays = useMemo(() => daysBetween(c.birthDate), [c.birthDate]);
  const ageInWeeks = ageInDays != null ? Math.floor(ageInDays / 7) : null;

  const caseNumeric = useMemo(() => ({
    birthWeight: num(c.birthWeight),
    currentWeight: num(c.currentWeight),
    minWeight: num(c.minWeight),
    minWeightDay: c.birthDate && c.minWeightDate ? daysFromBirth(c.birthDate, c.minWeightDate) : null,
    dischargeWeight: num(c.dischargeWeight),
    dischargeDay: num(c.dischargeDay),
    ageInDays,
    gestationalAgeWeeks: num(c.gestationalAgeWeeks),
    birthType: c.birthType,
    complications: c.complications,
    urineFreq: num(c.urineFreq),
    stoolFreq: num(c.stoolFreq),
    generalState: c.generalState,
  }), [c, ageInDays]);

  const weightsNumeric = useMemo(() =>
    c.weights.map(w => ({
      day: c.birthDate && w.date ? daysFromBirth(c.birthDate, w.date) : null,
      weight: num(w.weight),
      note: w.balanza === 'misma' ? 'Misma balanza' : w.balanza === 'otra' ? 'Otra balanza' : ''
    })).filter(w => w.day != null && w.weight != null),
  [c.weights, c.birthDate]);

  const findings = useMemo(() => interpret(caseNumeric, weightsNumeric), [caseNumeric, weightsNumeric]);

  // Cálculo pérdida máxima
  const lossPct = (caseNumeric.birthWeight && caseNumeric.minWeight)
    ? ((caseNumeric.birthWeight - caseNumeric.minWeight) / caseNumeric.birthWeight * 100)
    : null;

  // Cálculo ganancia media diaria post-recuperación
  const dailyGain = useMemo(() => {
    const bw = caseNumeric.birthWeight;
    const cw = caseNumeric.currentWeight;
    if (!bw || !cw || ageInDays == null) return null;
    // Encontrar día de recuperación del peso de nacimiento
    const recovery = weightsNumeric.find(w =>
      w.weight >= bw && w.day > (caseNumeric.minWeightDay || 0)
    );
    if (!recovery || ageInDays <= recovery.day) return null;
    const daysPost = ageInDays - recovery.day;
    if (daysPost <= 0) return null;
    const gramsGained = cw - recovery.weight;
    const perDay = gramsGained / daysPost;
    // Rango esperado según edad
    const range = ageInDays < 90
      ? { min: 20, ideal: 30 }
      : ageInDays < 180 ? { min: 15, ideal: 20 } : { min: 8, ideal: 12 };
    let status = 'below';
    let statusLabel = '';
    let statusColor = '#B44343';
    if (perDay >= range.ideal) { status = 'ideal'; statusLabel = 'Muy buen ritmo'; statusColor = '#6B7C66'; }
    else if (perDay >= range.min) { status = 'ok'; statusLabel = 'Dentro del rango esperado'; statusColor = '#8FA08A'; }
    else if (perDay > 0) { statusLabel = `Por debajo del rango (min ${range.min} g/día)`; statusColor = '#B8806E'; }
    else { statusLabel = 'Sin ganancia'; statusColor = '#B44343'; }
    return {
      perDay: Math.round(perDay * 10) / 10,
      range,
      status,
      statusLabel,
      statusColor,
      daysPost,
      recoveryDay: recovery.day,
    };
  }, [caseNumeric, weightsNumeric, ageInDays]);

  const handlePrint = () => window.print();
  const handleReset = () => {
    if (window.confirm('¿Borrar los datos del caso actual?')) setC(emptyCase());
  };

  if (!unlocked) return <Gate onUnlock={() => setUnlocked(true)}/>;

  return (
    <div className="min-h-screen bg-[#F4EDE2] text-[#2E2A23]" data-testid="lact-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#F4EDE2]/95 backdrop-blur-md border-b border-[#DBCEB4] print:hidden">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[#B8806E] text-[#FAF4E8] grid place-items-center font-serif text-base leading-none">k</span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-[16px]">Asesoría de lactancia</span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#8A7E6E]">uso profesional</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={handleReset} className="btn-ghost !py-2 !px-4 text-[11px] tracking-[0.12em]" data-testid="lact-reset">
              <RefreshCw size={13} strokeWidth={1.8}/> Nuevo caso
            </button>
            <button onClick={handlePrint} className="btn-primary !py-2 !px-4 text-[11px] tracking-[0.12em]" data-testid="lact-print">
              <Printer size={13} strokeWidth={1.8}/> Imprimir / PDF
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8 grid lg:grid-cols-[1fr,520px] gap-8">
        {/* ── Left: FORM ── */}
        <div className="space-y-8">
          {/* Datos básicos */}
          <section className="rounded-3xl bg-[#FAF4E8] border border-[#DBCEB4] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Baby size={16} strokeWidth={1.8} className="text-[#B8806E]"/>
              <h2 className="font-serif text-[20px]">Datos del bebé</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nombre / referencia">
                <TextInput value={c.babyName} onChange={(v) => set('babyName', v)} placeholder="Ej: bebé de María"/>
              </Field>
              <Field label="Sexo">
                <Select value={c.babySex} onChange={(v) => set('babySex', v)} options={[
                  { value: 'F', label: 'Femenino' }, { value: 'M', label: 'Masculino' },
                ]}/>
              </Field>
              <Field label="Fecha de nacimiento" hint={ageInDays != null ? `${ageInDays} días · ${ageInWeeks} sem` : ''}>
                <input type="date" className={inputCls} value={c.birthDate} onChange={(e) => set('birthDate', e.target.value)}/>
              </Field>
              <Field label="Edad gestacional al nacer (semanas)">
                <NumberInput value={c.gestationalAgeWeeks} onChange={(v) => set('gestationalAgeWeeks', v)} placeholder="Ej: 38" step="0.5" min="20" max="45"/>
              </Field>
              <Field label="Tipo de parto">
                <Select value={c.birthType} onChange={(v) => set('birthType', v)} options={[
                  { value: 'vaginal', label: 'Vaginal' },
                  { value: 'cesarea', label: 'Cesárea' },
                ]}/>
              </Field>
              <Field label="Tipo de alimentación actual">
                <Select value={c.feedingType} onChange={(v) => set('feedingType', v)} options={[
                  { value: 'exclusiva', label: 'Lactancia exclusiva' },
                  { value: 'mixta', label: 'Lactancia mixta' },
                  { value: 'formula', label: 'Solo fórmula' },
                ]}/>
              </Field>
              <Field label="Circunstancias del nacimiento" cols={2}
                hint="Marcá las que apliquen — el sistema las incorpora al análisis clínico.">
                <CompsBoxes value={c.complications} onChange={setComps}/>
              </Field>
            </div>
          </section>

          {/* Pesos */}
          <section className="rounded-3xl bg-[#FAF4E8] border border-[#DBCEB4] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Weight size={16} strokeWidth={1.8} className="text-[#B8806E]"/>
              <h2 className="font-serif text-[20px]">Pesos</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Peso al nacer (g)">
                <NumberInput value={c.birthWeight} onChange={(v) => set('birthWeight', v)} placeholder="3200"/>
              </Field>
              <Field label="Peso al alta (g)">
                <NumberInput value={c.dischargeWeight} onChange={(v) => set('dischargeWeight', v)} placeholder="3050"/>
              </Field>
              <Field label="Día del alta">
                <NumberInput value={c.dischargeDay} onChange={(v) => set('dischargeDay', v)} placeholder="Ej: 2"/>
              </Field>
              <Field label="Peso actual (g)">
                <NumberInput value={c.currentWeight} onChange={(v) => set('currentWeight', v)} placeholder="Ej: 3800"/>
              </Field>
              <Field label="Peso mínimo registrado (g)">
                <NumberInput value={c.minWeight} onChange={(v) => set('minWeight', v)} placeholder="Ej: 2980"/>
              </Field>
              <Field label="Fecha del peso mínimo"
                hint={c.birthDate && c.minWeightDate ? `día ${daysFromBirth(c.birthDate, c.minWeightDate)}` : 'el sistema calcula el día'}>
                <input type="date" className={inputCls} value={c.minWeightDate || ''} onChange={(e) => set('minWeightDate', e.target.value)}/>
              </Field>
              <Field label="Pesos posteriores" cols={2}
                hint="Día desde el nacimiento · peso en gramos · dónde se pesó (misma balanza es ideal).">
                <WeightsList weights={c.weights} onChange={(v) => set('weights', v)} birthDate={c.birthDate}/>
              </Field>
            </div>

            {/* Cálculos rápidos */}
            {(lossPct != null || dailyGain != null) && (
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {lossPct != null && (
                  <div className="rounded-2xl bg-[#F1ECE3] border border-[#DBCEB4] p-4">
                    <div className="text-[10.5px] tracking-[0.22em] uppercase text-[#8A7E6E]">Pérdida máxima</div>
                    <div className="font-serif text-[28px] leading-none text-[#B8806E] mt-1" data-testid="lact-loss-pct">
                      {lossPct.toFixed(1)}%
                    </div>
                    <div className="text-[11px] text-[#8A7E6E] mt-2">
                      (Peso nac. − peso mín.) / peso nac. × 100
                    </div>
                  </div>
                )}
                {dailyGain != null && (
                  <div className="rounded-2xl bg-[#F1ECE3] border border-[#DBCEB4] p-4" data-testid="lact-daily-gain">
                    <div className="text-[10.5px] tracking-[0.22em] uppercase text-[#8A7E6E]">Ganancia diaria</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-serif text-[28px] leading-none" style={{ color: dailyGain.statusColor }}>
                        {dailyGain.perDay} g/día
                      </span>
                    </div>
                    <div className="text-[11px] mt-2" style={{ color: dailyGain.statusColor }}>
                      {dailyGain.statusLabel}
                    </div>
                    <div className="text-[10.5px] text-[#8A7E6E] mt-1">
                      Esperado {dailyGain.range.min}–{dailyGain.range.ideal} g/día · desde día {dailyGain.recoveryDay} ({dailyGain.daysPost}d)
                    </div>
                  </div>
                )}
              </div>
            )}
            {lossPct != null && dailyGain == null && caseNumeric.currentWeight && (
              <p className="mt-3 text-[11.5px] text-[#8A7E6E] italic">
                Ganancia diaria: agregá un peso posterior donde el bebé haya recuperado
                el peso de nacimiento para calcular la ganancia media.
              </p>
            )}
          </section>

          {/* Eliminaciones y estado */}
          <section className="rounded-3xl bg-[#FAF4E8] border border-[#DBCEB4] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Droplet size={16} strokeWidth={1.8} className="text-[#B8806E]"/>
              <h2 className="font-serif text-[20px]">Eliminaciones y estado</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Micciones por día"
                hint="A partir del 5° día: ≥6/día = adecuado.">
                <NumberInput value={c.urineFreq} onChange={(v) => set('urineFreq', v)} placeholder="Ej: 6"/>
              </Field>
              <Field label="Deposiciones por día"
                hint="Primeras 6 semanas: ≥3-4/día es lo esperado.">
                <NumberInput value={c.stoolFreq} onChange={(v) => set('stoolFreq', v)} placeholder="Ej: 4"/>
              </Field>
              <Field label="Notas sobre eliminaciones" cols={2}>
                <textarea
                  className={inputCls + ' resize-none'}
                  rows="2"
                  value={c.eliminationNotes || ''}
                  onChange={(e) => set('eliminationNotes', e.target.value)}
                  placeholder="Ej: color, consistencia, evolución..."
                />
              </Field>
              <Field label="Estado general" cols={2}>
                <Select value={c.generalState} onChange={(v) => set('generalState', v)} options={[
                  { value: 'alerta', label: 'Alerta para las tomas' },
                  { value: 'somnoliento', label: 'Somnolencia excesiva' },
                  { value: 'cuesta_despertar', label: 'Cuesta despertarlo' },
                  { value: 'irritable', label: 'Irritable' },
                ]}/>
              </Field>
              <Field label="Notas clínicas adicionales" cols={2}>
                <textarea
                  className={inputCls + ' resize-none'}
                  rows="3"
                  value={c.notes || ''}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="Cualquier observación relevante..."
                />
              </Field>
            </div>
          </section>
        </div>

        {/* ── Right: RESULTS ── */}
        <aside className="lg:sticky lg:top-24 self-start space-y-5">
          <div className="rounded-3xl bg-[#FAF4E8] border border-[#DBCEB4] p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#8A7E6E]">Curva de peso</span>
            </div>
            <WeightChart caseData={c} weights={weightsNumeric} ageInDays={ageInDays}/>
            <div className="mt-3 flex items-center gap-4 text-[10.5px] text-[#8A7E6E] tracking-[0.06em]">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-[3px] bg-[#B8806E] rounded"/> Bebé
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-[2px] bg-[#6B7C66] rounded" style={{borderTop:'1px dashed #6B7C66'}}/> Esperado (banda verde)
              </span>
            </div>
          </div>

          <div>
            <span className="text-[10px] tracking-[0.28em] uppercase text-[#8A7E6E] block mb-2">
              Interpretación clínica
            </span>
            <FindingsBox findings={findings}/>
          </div>

          <div className="text-[10.5px] leading-[1.55] text-[#8A7E6E] italic border-t border-[#DBCEB4] pt-4">
            Herramienta de apoyo profesional para la Lic. Karina Negrin en su rol de
            asesora de lactancia. No sustituye evaluación pediátrica. Los cálculos son
            orientativos y deben ser interpretados clínicamente en cada caso individual.
          </div>
        </aside>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block px-8 py-6">
        <h1 className="font-serif text-2xl">Valoración de lactancia · {c.babyName || 'sin nombre'}</h1>
        <p className="text-xs text-[#8A7E6E] mt-1">
          Nacimiento: {fmtDate(c.birthDate)} · Edad: {ageInDays} días ({ageInWeeks} semanas)
          {c.gestationalAgeWeeks && ` · EG al nacer: ${c.gestationalAgeWeeks} semanas`}
        </p>
        <p className="text-[10px] text-[#8A7E6E] mt-3">
          Emitido por Lic. Karina Negrin · Asesora de lactancia · karinanegrin.com
        </p>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white; }
          .lg\\:sticky { position: static !important; }
          aside { break-before: page; }
          section { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
